/**
 * delay holds reference to the setTimeout
 */
export class Subtitles {
    private USE_DISPOSAL: boolean = false;
    private queue: any[] = []; // queue of script lines; entries are nulled, not removed, when unused
    private queueDisposal: number[] = []; // queue of script lines indexes to dispose of
    private domContainer: HTMLDivElement | null;
    private domScriptPositioner: HTMLDivElement;
    private domScript: HTMLDivElement;
    private domScriptLines: HTMLParagraphElement;
    private domSecondaryScriptLine: HTMLParagraphElement;
    private secondaryText: string | null = null;
    private secondaryDelay: number;
    private sidePadding: number = 20;
    private smallerThanBreakpoint: boolean;
    private domElements: HTMLElement[] = []; // any element used in this class, used for removal

    private testScript: any[] = [
        {
            text: "this is a very long sentence which is over 40 characters",
            delay: null
        },
        { text: "line 2", delay: null },
        { text: "line 3", delay: null },
        { text: "line 4", delay: null },
        { text: "line 5", delay: null }
    ];

    constructor(domContainer: HTMLDivElement, smallerThanBreakpoint: boolean) {
        this.domContainer = domContainer;
        this.smallerThanBreakpoint = smallerThanBreakpoint;
        this.createSubtitles();
    }

    /**
     * Tests the script running by causing a delay between lines.
     */
    public runScriptTest() {
        this.scriptTestDelay(0);
    }

    /**
     * Adds a line of text to the bottom of the subtitles.
     * @param text
     */
    public add(text: string, delay?: number, secondary?: boolean) {
        var index: number;
        var time = (typeof delay === "undefined") ? 3000 : delay; // short display time for short words
        if (typeof secondary === "undefined") secondary = false;

        if (secondary) {
            this.removeSecondary();
            this.secondaryText = text;
        } else {
            if (typeof delay === "undefined") {
                // set the timer for longer text
                if (text.length >= 40) time = 10000;
                else if (text.length >= 15) time = 6000;
            }

            // kill the last so there is only one
            this.removeDelay(0);
            this.queue = [];

            this.queue.push({ text: text, delay: null });
        }

        this.display();
        this.resize();

        if (secondary) {
            this.addSecondaryDelay((typeof delay === "undefined") ? time : delay);
        } else {
            index = this.queue.length - 1;

            // remove the text after a delay
            this.addDelay(index, time);
        }
    }

    public show(show?: boolean) {
        if (typeof show === "undefined") show = true;
        this.domScript.style.display = show ? "block" : "none";
    }

    /**
     * Resizes the script element, allowing internal script line elements to fill its space.
     * @param smallerThanBreakpoint - Allows resize to be called externally, changing the value.
     */
    public resize(smallerThanBreakpoint?: boolean) {
        if (typeof smallerThanBreakpoint !== "undefined") {
            this.smallerThanBreakpoint = smallerThanBreakpoint;
        }
        const w = Math.abs(0.55 * Math.max(window.innerWidth, window.innerHeight));
        this.domScript.style.width = w + "px";
        this.domScript.style.left = -(w * 0.5) + "px";
        this.resizeLines();
    }

    public dispose() {
        this.reset();
        this.disposeDOMElements();

        this.testScript = [];
        this.queue = [];
        this.queueDisposal = [];
        this.domContainer = null;
    }

    /**
     * Clears the subtitles, ready for reuse.
     */
    public reset() {
        let i: number;

        if (this.testScript) {
            for (i = 0; i < this.testScript.length; i++) {
                if (this.testScript[i].delay) {
                    clearTimeout(this.testScript[i].delay);
                    this.testScript[i].delay = null;
                }
            }
        }

        if (this.queue) {
            for (i = 0; i < this.queue.length; i++) {
                this.removeDelay(i);
            }
        }

        this.queue = [];
        this.queueDisposal = [];
        this.display();
    }

    /**
     * Remove the text after a delay.
     * @param index
     * @param time
     */
    private addDelay(index: number, time: number) {
        this.queue[index].delay = setTimeout(() => {
            this.remove(index);
        }, time);
    }

    private addSecondaryDelay(time: number) {
        this.secondaryDelay = window.setTimeout(() => this.removeSecondary(), time);
    }

    /**
     * Removes the delay applied to the specified script index.
     * @param index
     */
    private removeDelay(index: number) {
        if (this.queue && this.queue[index]) {
            if (this.queue[index].delay) {
                clearTimeout(this.queue[index].delay);
                this.queue[index].delay = null;
            }
        }
    }

    /**
     * Removes a line of text, specified by the index position, from the subtitles.
     * If there are any other lines still remaining above the index position, they
     * will be added to a disposal list instead. That disposal list will be cleared
     * up when the upper line is later cleared.
     * @param index
     */
    private remove(index: number) {
        let allowRemoval = true; // whether to allow the removal at this point

        // ensure that the position exists
        if (index < this.queue.length) {
            // find whether to stop the removal
            if (this.USE_DISPOSAL) {
                for (var i = 0; i < index; i++)
                    if (this.queue[i]) {
                        allowRemoval = false;
                    }
            }

            if (allowRemoval) {
                // remove the delay and text
                this.removeDelay(index);
                this.queue[index] = null;

                // remove any other lines which are waiting for disposal
                for (var j = this.queueDisposal.length - 1; j >= 0; j--) {
                    this.queue[this.queueDisposal[j]] = null;
                    this.queueDisposal.splice(j, 1);
                }

                this.display();
            } else {
                // remove the delay
                this.removeDelay(index);

                // add to the disposal list
                this.queueDisposal.push(index);
            }
        }
    }

    private removeSecondary() {
        this.secondaryText = null;
        clearTimeout(this.secondaryDelay);
        this.display();
    }

    /**
     * Display the current list of text, clearing the current display.
     */
    private display() {
        var allowDisplay = false;
        var firstDone = false;

        // clear the current display
        this.domScriptLines.innerHTML = "";
        this.domSecondaryScriptLine.innerHTML = "";

        // add all scripts
        if (this.queue) {
            for (var i = 0; i < this.queue.length; i++) {
                if (this.queue[i]) {
                    if (firstDone) {
                        this.domScriptLines.appendChild(document.createElement("br"));
                    }

                    this.domScriptLines.appendChild(document.createTextNode(this.queue[i].text));
                    firstDone = true;
                    allowDisplay = true;
                }
            }
        }

        if (this.secondaryText) {
            this.domSecondaryScriptLine.appendChild(document.createTextNode(this.secondaryText));
        }

        this.resizeLines();

        // display the text only if there is something present
        this.domScriptLines.style.display = allowDisplay ? "block" : "none";
        this.domSecondaryScriptLine.style.display = this.secondaryText ? "block" : "none";
    }

    private createSubtitles() {
        this.domScriptPositioner = document.createElement("div");
        this.domScriptPositioner.setAttribute("id", "subtitles-positioner");
        if (this.domContainer) this.domContainer.appendChild(this.domScriptPositioner);
        this.domElements.push(this.domScriptPositioner);

        this.domScript = document.createElement("div");
        this.domScript.setAttribute("id", "subtitles");
        this.domScriptPositioner.appendChild(this.domScript);
        this.domElements.push(this.domScript);

        this.domSecondaryScriptLine = document.createElement("p");
        this.domSecondaryScriptLine.style.paddingLeft = this.sidePadding + "px";
        this.domSecondaryScriptLine.style.paddingRight = this.sidePadding + "px";
        this.domSecondaryScriptLine.classList.add("secondary");
        this.domScript.appendChild(this.domSecondaryScriptLine);
        this.domElements.push(this.domSecondaryScriptLine);

        this.domScriptLines = document.createElement("p");
        this.domScriptLines.style.paddingLeft = this.sidePadding + "px";
        this.domScriptLines.style.paddingRight = this.sidePadding + "px";
        this.domScript.appendChild(this.domScriptLines);
        this.domElements.push(this.domScriptLines);

        this.resize();
    }

    private resizeLines() {
        let paddedWidth; // width including the padding
        let innerWidth; // width applied, ignoring the extra padding

        if (this.domScriptLines) {
            if (this.smallerThanBreakpoint) {
                this.domScriptLines.classList.add("small");
                this.domSecondaryScriptLine.classList.add("small");
            } else {
                this.domScriptLines.classList.remove("small");
                this.domSecondaryScriptLine.classList.remove("small");
            }

            // set to the full width to begin
            this.domScriptLines.style.width = "100%";
            this.domSecondaryScriptLine.style.width = "100%";

            // find the inner width
            paddedWidth = this.domScriptLines.clientWidth;
            innerWidth = paddedWidth - this.sidePadding * 2;

            // reduce the applied width so that the padding is included in the width calculation
            this.domScriptLines.style.width = innerWidth - this.sidePadding * 2 + "px";
            this.domSecondaryScriptLine.style.width = innerWidth - this.sidePadding * 2 + "px";
        }
    }

    private disposeDOMElements() {
        for (var i = 0; i < this.domElements.length; i++) {
            const element: HTMLElement = this.domElements[i];
            if (element && element.parentNode) {
                element.parentNode.removeChild(element);
            }
        }
        this.domElements = [];
    }

    /**
     * Applies the delay to the script test.
     * @param index
     */
    private scriptTestDelay(index: number) {
        var time = 1000 + Math.floor(Math.random() * 1000);
        this.testScript[index].delay = setTimeout(() => {
            this.scriptTestAdd(index);
        }, time);
    }

    private scriptTestAdd(index: number) {
        // add the text
        this.add(this.testScript[index].text);

        // run the next line, if available
        if (index < this.testScript.length - 1) this.scriptTestDelay(index + 1);
    }
}
